import React, { useState, useEffect, useMemo, useRef } from "react";
import "./GoalsManagement.css";
import {
  Goal,
  Domain,
  User,
  createGoal,
  updateGoal,
  deleteGoal,
  approveGoal,
  rejectGoal,
  GoalCreate,
  GoalUpdate,
} from "../services/treatmentGoals";

interface GoalsManagementProps {
  domain: Domain | null;
  goals: Goal[];
  onRefresh: () => void;
  currentUser: any;
  authors: User[];
}

interface GoalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (goalData: GoalCreate | GoalUpdate) => void;
  goal?: Goal;
  domain: Domain;
  loading: boolean;
}

const GoalDialog: React.FC<GoalDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  goal,
  domain,
  loading,
}) => {
  const [goalText, setGoalText] = useState("");

  useEffect(() => {
    if (isOpen) {
      setGoalText(goal?.goal_text || "");
    }
  }, [isOpen, goal]);

  const handleSave = () => {
    if (!goalText.trim()) return;

    if (goal) {
      // Editing existing goal
      onSave({ goal_text: goalText });
    } else {
      // Creating new goal
      onSave({
        domain_id: domain.id,
        goal_text: goalText,
      });
    }
  };

  const handleClose = () => {
    setGoalText("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>{goal ? "Edit Goal" : "Add New Goal"}</h3>
          <button className="dialog-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="dialog-content">
          <div className="form-group">
            <label>Domain</label>
            <input type="text" value={domain.name} disabled />
          </div>

          <div className="form-group">
            <label>Goal Text</label>
            <textarea
              placeholder="Enter goal description..."
              rows={6}
              value={goalText}
              onChange={(e) => setGoalText(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="dialog-actions">
          <button
            className="btn-cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={loading || !goalText.trim()}
          >
            {loading ? "Saving..." : goal ? "Update Goal" : "Create Goal"}
          </button>
        </div>
      </div>
    </div>
  );
};

const GoalsManagement: React.FC<GoalsManagementProps> = ({
  domain,
  goals,
  onRefresh,
  currentUser,
  authors,
}) => {
  // State for each domain's filters and pagination
  const [domainStates, setDomainStates] = useState<
    Record<
      number,
      {
        searchTerm: string;
        selectedAuthorId: number | null;
        authorSearchTerm: string;
        statusFilter: "all" | "approved" | "pending";
        currentPage: number;
      }
    >
  >({});

  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const authorDropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 10;

  // Get current domain's state or create default
  const getCurrentDomainState = () => {
    if (!domain)
      return {
        searchTerm: "",
        selectedAuthorId: null,
        authorSearchTerm: "",
        statusFilter: "all" as const,
        currentPage: 1,
      };
    return (
      domainStates[domain.id] || {
        searchTerm: "",
        selectedAuthorId: null,
        authorSearchTerm: "",
        statusFilter: "all" as const,
        currentPage: 1,
      }
    );
  };

  const currentState = getCurrentDomainState();
  const searchTerm = currentState.searchTerm;
  const selectedAuthorId = currentState.selectedAuthorId;
  const authorSearchTerm = currentState.authorSearchTerm;
  const statusFilter = currentState.statusFilter;
  const currentPage = currentState.currentPage;

  // Update domain state
  const updateDomainState = (updates: Partial<typeof currentState>) => {
    if (!domain) return;
    setDomainStates((prev) => ({
      ...prev,
      [domain.id]: { ...currentState, ...updates },
    }));
  };
  const [showDialog, setShowDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const authorSearchRef = useRef<HTMLDivElement>(null);

  const isSuperuser = currentUser?.is_superuser || false;

  // No need to reset state when domain changes - we maintain per-domain state

  // Get selected author object
  const selectedAuthor = authors.find((a) => a.id === selectedAuthorId);

  // Filter authors based on search term
  const filteredAuthors = useMemo(() => {
    if (!authorSearchTerm) return authors;
    const searchLower = authorSearchTerm.toLowerCase();
    return authors.filter((author) =>
      author.full_name.toLowerCase().includes(searchLower)
    );
  }, [authors, authorSearchTerm]);

  // Close dropdown when clicking outside and clear text if no selection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        authorSearchRef.current &&
        !authorSearchRef.current.contains(event.target as Node)
      ) {
        setShowAuthorDropdown(false);
        setHighlightedIndex(-1);
        // Clear text if no author is selected
        if (!selectedAuthorId && authorSearchTerm) {
          updateDomainState({ authorSearchTerm: "" });
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [authorSearchTerm, selectedAuthorId]);

  // Auto-scroll to highlighted item
  useEffect(() => {
    if (highlightedIndex >= 0 && authorDropdownRef.current) {
      const highlightedElement = authorDropdownRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  // Filter goals based on search term, author, and status
  const filteredGoals = useMemo(() => {
    let filtered = goals;

    // Filter by author
    if (selectedAuthorId) {
      filtered = filtered.filter((goal) => goal.author.id === selectedAuthorId);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (goal) =>
          goal.goal_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
          goal.author.full_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter === "approved") {
      filtered = filtered.filter((goal) => goal.is_approved);
    } else if (statusFilter === "pending") {
      filtered = filtered.filter((goal) => !goal.is_approved);
    }

    return filtered;
  }, [goals, searchTerm, selectedAuthorId, statusFilter]);

  // Paginate filtered goals
  const paginatedGoals = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredGoals.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredGoals, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredGoals.length / itemsPerPage);

  const handleAddGoal = () => {
    setEditingGoal(null);
    setShowDialog(true);
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setShowDialog(true);
  };

  const handleSaveGoal = async (goalData: GoalCreate | GoalUpdate) => {
    if (!domain) return;

    setError("");
    setLoading(true);

    try {
      if (editingGoal) {
        await updateGoal(editingGoal.id, goalData as GoalUpdate);
      } else {
        await createGoal(goalData as GoalCreate);
      }

      setShowDialog(false);
      setEditingGoal(null);
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to save goal");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (goalId: number) => {
    if (!confirm("Are you sure you want to delete this goal?")) return;

    try {
      await deleteGoal(goalId);
      onRefresh();

      // Adjust current page if needed
      const newTotalPages = Math.ceil(
        (filteredGoals.length - 1) / itemsPerPage
      );
      if (currentPage > newTotalPages && newTotalPages > 0) {
        updateDomainState({ currentPage: newTotalPages });
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete goal");
    }
  };

  const handleApproveGoal = async (goalId: number) => {
    try {
      await approveGoal(goalId);
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to approve goal");
    }
  };

  const handleRejectGoal = async (goalId: number) => {
    if (!confirm("Are you sure you want to reject this goal?")) return;

    try {
      await rejectGoal(goalId);
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to reject goal");
    }
  };

  const canEditGoal = (goal: Goal) => {
    return isSuperuser || goal.author.id === currentUser?.id;
  };

  const canDeleteGoal = (goal: Goal) => {
    return isSuperuser || goal.author.id === currentUser?.id;
  };

  const handlePageChange = (page: number) => {
    updateDomainState({ currentPage: page });
  };

  const handleAuthorSelect = (author: User) => {
    updateDomainState({
      selectedAuthorId: author.id,
      authorSearchTerm: author.full_name,
      currentPage: 1,
    });
    setShowAuthorDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleClearAuthor = () => {
    updateDomainState({
      selectedAuthorId: null,
      authorSearchTerm: "",
      currentPage: 1,
    });
    setShowAuthorDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleAuthorSearchChange = (value: string) => {
    updateDomainState({
      authorSearchTerm: value,
      selectedAuthorId: null, // Clear selection when typing
      currentPage: 1,
    });
    setShowAuthorDropdown(true);
    // Auto-highlight first item when typing
    setHighlightedIndex(value ? 0 : -1);
  };

  const handleAuthorKeyDown = (e: React.KeyboardEvent) => {
    if (!showAuthorDropdown || filteredAuthors.length === 0) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        setShowAuthorDropdown(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredAuthors.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredAuthors.length
        ) {
          handleAuthorSelect(filteredAuthors[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowAuthorDropdown(false);
        setHighlightedIndex(-1);
        // Clear text if no selection
        if (!selectedAuthorId) {
          updateDomainState({ authorSearchTerm: "" });
        }
        break;
      case "Tab":
        // Allow tab to select highlighted item
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredAuthors.length
        ) {
          e.preventDefault();
          handleAuthorSelect(filteredAuthors[highlightedIndex]);
        } else {
          setShowAuthorDropdown(false);
        }
        break;
    }
  };

  if (!domain) {
    return (
      <div className="goals-management">
        <div className="goals-empty">
          <h2>Select a Domain</h2>
          <p>Choose a domain from the sidebar to view and manage its goals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="goals-management">
      {error && <div className="error-banner">{error}</div>}

      <div className="goals-container">
        <div className="goals-controls">
          <div className="goals-controls-left">
            <div className="filter-group">
              <label className="filter-label">Search Goals:</label>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Type goal text here..."
                  value={searchTerm}
                  onChange={(e) => {
                    updateDomainState({
                      searchTerm: e.target.value,
                      currentPage: 1,
                    });
                  }}
                  className="search-input"
                  autoComplete="off"
                />
                {searchTerm && (
                  <button
                    className="clear-search-btn"
                    onClick={() => {
                      updateDomainState({
                        searchTerm: "",
                        currentPage: 1,
                      });
                    }}
                    title="Clear search"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 1L1 13M1 1L13 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Filter by Author:</label>
              <div className="author-search-wrapper" ref={authorSearchRef}>
                <input
                  type="text"
                  placeholder="Type author name here..."
                  value={authorSearchTerm}
                  onChange={(e) => handleAuthorSearchChange(e.target.value)}
                  onKeyDown={handleAuthorKeyDown}
                  onFocus={() => setShowAuthorDropdown(true)}
                  className="author-search-input"
                  autoComplete="off"
                />
                {authorSearchTerm && (
                  <button
                    className="clear-author-btn"
                    onClick={handleClearAuthor}
                    title="Clear filter"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 1L1 13M1 1L13 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                {showAuthorDropdown && (
                  <div className="author-dropdown" ref={authorDropdownRef}>
                    {filteredAuthors.length > 0 ? (
                      filteredAuthors.map((author, index) => (
                        <div
                          key={author.id}
                          className={`author-dropdown-item ${
                            author.id === selectedAuthorId ? "selected" : ""
                          } ${index === highlightedIndex ? "highlighted" : ""}`}
                          onClick={() => handleAuthorSelect(author)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          {author.full_name}
                          {author.id === selectedAuthorId && (
                            <span className="check-icon">✓</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="author-dropdown-empty">
                        No authors found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Filter by Status:</label>
              <div className="status-filter-wrapper">
                <button
                  className={`status-filter-btn ${
                    statusFilter === "all" ? "active" : ""
                  }`}
                  onClick={() => {
                    updateDomainState({
                      statusFilter: "all",
                      currentPage: 1,
                    });
                  }}
                >
                  All
                </button>
                <button
                  className={`status-filter-btn ${
                    statusFilter === "approved" ? "active" : ""
                  }`}
                  onClick={() => {
                    updateDomainState({
                      statusFilter: "approved",
                      currentPage: 1,
                    });
                  }}
                >
                  Approved
                </button>
                <button
                  className={`status-filter-btn ${
                    statusFilter === "pending" ? "active" : ""
                  }`}
                  onClick={() => {
                    updateDomainState({
                      statusFilter: "pending",
                      currentPage: 1,
                    });
                  }}
                >
                  Pending
                </button>
              </div>
            </div>
          </div>

          <button className="btn btn-primary" onClick={handleAddGoal}>
            Add Goal
          </button>
        </div>

        <div className="goals-table-wrapper">
          <table className="goals-table">
            <thead>
              <tr>
                <th className="col-id">No</th>
                <th className="col-goal">Goal Text</th>
                <th className="col-author">Authored By</th>
                <th className="col-status">Status</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGoals.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan={5} className="empty-cell">
                    <div className="empty-message">
                      {searchTerm ||
                      selectedAuthor ||
                      statusFilter !== "all" ? (
                        <>
                          <p>No goals found matching your filters</p>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              updateDomainState({
                                searchTerm: "",
                                selectedAuthorId: null,
                                authorSearchTerm: "",
                                statusFilter: "all",
                                currentPage: 1,
                              });
                            }}
                          >
                            Clear Filters
                          </button>
                        </>
                      ) : (
                        <>
                          <p>No goals in this domain yet.</p>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={handleAddGoal}
                          >
                            + Add First Goal
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedGoals.map((goal, index) => (
                  <tr
                    key={goal.id}
                    className={!goal.is_approved ? "pending-approval" : ""}
                  >
                    <td className="col-id">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="col-goal">
                      <div className="goal-text-cell">{goal.goal_text}</div>
                    </td>
                    <td className="col-author">
                      <div className="author-cell">
                        <span className="author-name">
                          {goal.author.full_name}
                        </span>
                      </div>
                    </td>
                    <td className="col-status">
                      {goal.is_approved ? (
                        <span
                          className="status-badge approved"
                          title={`Approved by ${
                            goal.approved_by?.full_name || "Unknown"
                          }`}
                        >
                          ✓ Approved
                        </span>
                      ) : (
                        <span className="status-badge pending">⏳ Pending</span>
                      )}
                    </td>
                    <td className="col-actions">
                      <div className="action-buttons">
                        {isSuperuser && !goal.is_approved && (
                          <button
                            className="btn-icon approve"
                            onClick={() => handleApproveGoal(goal.id)}
                            title="Approve goal"
                          >
                            ✓
                          </button>
                        )}
                        {isSuperuser && goal.is_approved && (
                          <button
                            className="btn-icon reject"
                            onClick={() => handleRejectGoal(goal.id)}
                            title="Unapprove goal"
                          >
                            ✗
                          </button>
                        )}
                        {canEditGoal(goal) && (
                          <button
                            className="btn-icon edit"
                            onClick={() => handleEditGoal(goal)}
                            title="Edit goal"
                          >
                            ✏️
                          </button>
                        )}
                        {canDeleteGoal(goal) && (
                          <button
                            className="btn-icon delete"
                            onClick={() => handleDeleteGoal(goal.id)}
                            title="Delete goal"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="pagination-info">
            {filteredGoals.length > 0 ? (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredGoals.length)} of{" "}
                {filteredGoals.length} goals
              </>
            ) : (
              "No goals to display"
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  // Show first page, last page, current page, and pages around current
                  return (
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 2
                  );
                })
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="pagination-ellipsis">...</span>
                    )}
                    <button
                      className={`pagination-btn ${
                        page === currentPage ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <GoalDialog
        isOpen={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditingGoal(null);
        }}
        onSave={handleSaveGoal}
        goal={editingGoal || undefined}
        domain={domain}
        loading={loading}
      />
    </div>
  );
};

export default GoalsManagement;
